<script setup>
// Menu Meta -> Disparo (id 1 + e-mails MetaAccess). MULTI-CAIXA (Kairo
// 10/07/2026): seleciona 1+ caixas WhatsApp Cloud (com a saude da conta na
// frente de cada numero), o template e casado pelo NOME entre as caixas e a
// lista (CSV) e dividida em rodizio entre elas — tudo no MESMO formulario da
// aba Disparos (DisparosPage em modo multi via prop :inboxes).
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxHealthAPI from 'dashboard/api/inboxHealth';
import DisparosPage from '../settings/inbox/settingsPage/DisparosPage.vue';

const store = useStore();
const inboxesGetter = useMapGetter('inboxes/getInboxes');

const cloudInboxes = computed(() =>
  (inboxesGetter.value || []).filter(
    i =>
      i.channel_type === 'Channel::Whatsapp' &&
      i.provider === 'whatsapp_cloud' &&
      // DESATIVAR CAIXA (v76): fora das funcoes Template/Disparo do menu Meta
      !i.meta_disabled &&
      // v92: caixas OCULTADAS (Configuracoes -> Ocultar) tambem ficam fora
      !i.hidden
  )
);

// --- selecao + saude (mesmo padrao do Meta -> Template) ---------------------
const selected = ref({});
const healthStatus = ref({});
const isLoadingHealth = ref(false);

const selectedBoxes = computed(() =>
  cloudInboxes.value.filter(
    i => selected.value[i.id] && healthStatus.value[i.id]?.ok
  )
);

const QUALITY_DOT = {
  GREEN: 'bg-n-teal-9',
  YELLOW: 'bg-n-amber-9',
  RED: 'bg-n-ruby-9',
};
const QUALITY_LABEL = {
  GREEN: 'Saúde alta',
  YELLOW: 'Saúde média',
  RED: 'Saúde baixa',
};

const healthBadge = box => {
  const h = healthStatus.value[box.id];
  if (!h) return { dot: 'bg-n-slate-8', text: 'verificando…', title: '' };
  if (!h.ok)
    return { dot: 'bg-n-ruby-9', text: 'indisponível', title: h.error || '' };
  const q = (h.quality || '').toUpperCase();
  return {
    dot: QUALITY_DOT[q] || 'bg-n-slate-8',
    text: `${QUALITY_LABEL[q] || h.quality || '—'}${h.tier ? ` · ${h.tier}` : ''}`,
    title: '',
  };
};

const loadHealth = async () => {
  isLoadingHealth.value = true;
  await Promise.all(
    cloudInboxes.value.map(async box => {
      try {
        const { data } = await InboxHealthAPI.getHealthStatus(box.id);
        healthStatus.value[box.id] = {
          ok: true,
          phone: data.display_phone_number || box.phone_number || '',
          quality: data.quality_rating || '—',
          tier: data.messaging_limit_tier || '',
        };
      } catch (e) {
        healthStatus.value[box.id] = {
          ok: false,
          error: e?.response?.data?.error || e.message,
        };
      }
    })
  );
  isLoadingHealth.value = false;
};

onMounted(async () => {
  await store.dispatch('inboxes/get');
  loadHealth();
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-surface-1 p-8">
    <div class="max-w-7xl w-full mx-auto">
      <div class="mb-6">
        <h1 class="text-heading-1 text-n-slate-12 mb-1">Meta — Disparo</h1>
        <p class="text-n-slate-11 text-sm mb-4">
          Selecione uma ou mais caixas — a lista é dividida entre elas e o
          template é casado pelo nome. A saúde da conta aparece na frente de
          cada número.
        </p>

        <div class="rounded-lg border border-n-weak p-4">
          <div class="flex items-center justify-between mb-3 flex-wrap gap-2">
            <p class="text-sm font-medium text-n-slate-12">
              Caixas de entrada (WhatsApp Cloud):
            </p>
            <NextButton
              size="xs"
              variant="outline"
              color="slate"
              label="Reverificar saúde"
              :is-loading="isLoadingHealth"
              @click="loadHealth"
            />
          </div>
          <div class="grid grid-cols-2 lg:grid-cols-3 gap-2">
            <label
              v-for="box in cloudInboxes"
              :key="box.id"
              class="flex items-center gap-2 text-sm text-n-slate-12"
              :class="
                healthStatus[box.id]?.ok === false
                  ? 'opacity-60 cursor-not-allowed'
                  : 'cursor-pointer'
              "
            >
              <Checkbox
                v-model="selected[box.id]"
                :disabled="healthStatus[box.id]?.ok === false"
              />
              <span>{{ box.name }}</span>
              <span class="text-xs text-n-slate-10">
                {{ healthStatus[box.id]?.phone || box.phone_number }}
              </span>
              <span
                class="flex items-center gap-1 text-xs text-n-slate-11 ml-auto"
                :title="healthBadge(box).title"
              >
                <span
                  :class="`inline-block w-2 h-2 rounded-full ${healthBadge(box).dot}`"
                />
                {{ healthBadge(box).text }}
              </span>
            </label>
          </div>
          <p v-if="selectedBoxes.length" class="text-xs text-n-teal-11 mt-3">
            ✓ {{ selectedBoxes.length }} caixa(s) selecionada(s) para o lote.
          </p>
        </div>
      </div>

      <DisparosPage
        v-if="selectedBoxes.length"
        :inboxes="selectedBoxes"
        class="!mx-0"
      />
      <div
        v-else
        class="text-sm text-n-slate-11 py-8 text-center border border-dashed border-n-weak rounded-lg"
      >
        Selecione ao menos uma caixa saudável para montar o disparo.
      </div>
    </div>
  </div>
</template>
